import { GridCellEditCommitParams } from '@/global/components/DataGrid/utils/types'
import { FormField } from '@/global/components/Form'
import { SimpleTabs } from '@/global/components/Tabs'
import Grid from '@/global/components/ui/Grid'
import ContentGuide from '@/global/guides/ContentGuide'
import { TrocaInicioPonto } from '@/service/api'
import { PeriodoPontoFactory } from '@acme/common'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'sonner'
import { useEditarEmpresaOutletContext } from '../../../modals/Editar'
import BlocosFechamento from './BlocoFechamentos'
import SliderDias from './SliderDias'

interface IProps {
    trocasInicioPonto: TrocaInicioPonto[]
}

export default function FormAlterarInicioPonto({ trocasInicioPonto }: IProps) {
    const { setValue } = useFormContext()

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <FormField
                    variant="filled"
                    label="Dia de início do ponto da competência anterior"
                    name="diaAnterior"
                    disabled
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormField
                    name="diaInicioPonto"
                    label="Novo dia de início do ponto"
                    variant="filled"
                    type="number"
                    min={1}
                    max={31}
                />
            </Grid>
            <Grid item xs={12}>
                <SimpleTabs
                    onChange={() => setValue('maximoDiasCompetencia', 31)}
                    tabs={[
                        {
                            content: (
                                <TabAutomatico
                                    trocasInicioPonto={trocasInicioPonto}
                                />
                            ),
                            label: 'Automático',
                            value: 'automatico',
                        },
                        {
                            content: (
                                <TabManual
                                    trocasInicioPonto={trocasInicioPonto}
                                />
                            ),
                            label: 'Manual',
                            value: 'manual',
                        },
                    ]}
                />
            </Grid>
        </Grid>
    )
}

function TabAutomatico({ trocasInicioPonto }: IProps) {
    return (
        <Grid container spacing={4} className="mt-2">
            <Grid item xs={6}>
                <SliderDias />
            </Grid>
            <Grid item xs={12}>
                <div className="w-full flex flex-col">
                    <h4 className="font-semibold">Próximos fechamentos</h4>
                    <div className="w-full flex flex-col *:flex *flex-col gap-4 mt-2">
                        <BlocosFechamento
                            trocasInicioPonto={trocasInicioPonto}
                        />
                    </div>
                </div>
            </Grid>
        </Grid>
    )
}

function TabManual({ trocasInicioPonto }: IProps) {
    const {
        data: { competenciaAtual, diaInicioPonto },
    } = useEditarEmpresaOutletContext()
    const isLoading = !competenciaAtual || !diaInicioPonto

    const { watch, setValue } = useFormContext()
    const novoDiaInicioPonto = watch('diaInicioPonto')

    const trocas = useMemo(() => {
        return {
            date: trocasInicioPonto.map(it => ({
                ...it,
                dataInicial: dayjs.utc(it.dataInicial).toDate(),
                dataFinal: dayjs.utc(it.dataFinal).toDate(),
            })),
            string: trocasInicioPonto.map(it => ({
                ...it,
                dataInicial: dayjs.utc(it.dataInicial).format('YYYY-MM-DD'),
                dataFinal: dayjs.utc(it.dataFinal).format('YYYY-MM-DD'),
            })),
        }
    }, [trocasInicioPonto])

    const defaultRows = useMemo(
        () =>
            Array.from({ length: 3 }).map((_, index) => {
                const competencia = dayjs(competenciaAtual).add(index, 'month'),
                    troca = trocas.string.find(
                        t => t.competencia === competencia.format('YYYY-MM'),
                    ),
                    id = competencia.format('YYYY-MM')
                if (troca) {
                    return {
                        id,
                        competencia: id,
                        dataInicial: troca.dataInicial,
                        dataFinal: troca.dataFinal,
                        index,
                    }
                }

                const [dataInicialNovo, dataFinalNovo] =
                    PeriodoPontoFactory.fromCompetencia(id, {
                        diaInicioPonto: novoDiaInicioPonto,
                        trocasInicioPonto: trocas.date,
                    }).getPeriodo()
                const [dataInicial] = PeriodoPontoFactory.fromCompetencia(id, {
                    diaInicioPonto: diaInicioPonto,
                    trocasInicioPonto: trocas.date,
                }).getPeriodo()
                return {
                    id,
                    competencia: id,
                    dataInicial:
                        index === 0
                            ? dataInicial.format('YYYY-MM-DD')
                            : dataInicialNovo.format('YYYY-MM-DD'),
                    dataFinal: dataFinalNovo.format('YYYY-MM-DD'),
                    index,
                }
            }),
        [novoDiaInicioPonto, competenciaAtual, trocas, diaInicioPonto],
    )

    const [rows, setRows] = useState<typeof defaultRows>([])

    useEffect(() => {
        setRows(defaultRows)
    }, [novoDiaInicioPonto])

    function onCellEditCommit({
        id,
        field,
        value,
    }: GridCellEditCommitParams<(typeof rows)[0]>) {
        const DIAS_MAXIMOS_MANUAL = 45

        setRows(prevRows => {
            const rowIndex = prevRows.findIndex(r => r.id === id)
            const currentRow = prevRows[rowIndex]
            const newRows = [...prevRows]

            const updatedRow = {
                ...currentRow,
                [field]: value,
            }

            const dataInicial =
                field === 'dataInicial' ? value : currentRow.dataInicial
            const dataFinal =
                field === 'dataFinal' ? value : currentRow.dataFinal

            if (dayjs(dataInicial).isAfter(dayjs(dataFinal))) {
                toast.error(
                    `Data inicial não pode ser maior que data final. 
                    Os valores não foram alterados.`,
                )
                return structuredClone(prevRows)
            }

            const totalDias =
                dayjs(dataFinal).diff(dayjs(dataInicial), 'day') + 1
            if (totalDias > DIAS_MAXIMOS_MANUAL) {
                toast.error(
                    `A competência não pode ter mais de ${DIAS_MAXIMOS_MANUAL} dias. 
                    Os valores não foram alterados.`,
                )
                return structuredClone(prevRows)
            }

            newRows[rowIndex] = updatedRow

            if (field === 'dataFinal' && rowIndex < prevRows.length - 1) {
                const nextDataInicial = dayjs(value)
                    .add(1, 'day')
                    .format('YYYY-MM-DD')
                const nextRow = newRows[rowIndex + 1]
                const nextTotalDias =
                    dayjs(nextRow.dataFinal).diff(
                        dayjs(nextDataInicial),
                        'day',
                    ) + 1

                if (nextTotalDias > DIAS_MAXIMOS_MANUAL || nextTotalDias < 1) {
                    toast.error(
                        `A próxima competência não pode ter mais de ${DIAS_MAXIMOS_MANUAL} dias. 
                        Os valores não foram alterados.`,
                    )
                    return structuredClone(prevRows)
                }

                newRows[rowIndex + 1] = {
                    ...nextRow,
                    dataInicial: nextDataInicial,
                } as typeof updatedRow
            }

            if (field === 'dataInicial' && rowIndex > 0) {
                const prevDataFinal = dayjs(value)
                    .subtract(1, 'day')
                    .format('YYYY-MM-DD')
                const prevRow = newRows[rowIndex - 1]
                const prevTotalDias =
                    dayjs(prevDataFinal).diff(
                        dayjs(prevRow.dataInicial),
                        'day',
                    ) + 1

                if (prevTotalDias > DIAS_MAXIMOS_MANUAL || prevTotalDias < 1) {
                    toast.error(
                        `A competência anterior não pode ter mais de ${DIAS_MAXIMOS_MANUAL} dias. 
                        Os valores não foram alterados.`,
                    )
                    return structuredClone(prevRows)
                }

                newRows[rowIndex - 1] = {
                    ...prevRow,
                    dataFinal: prevDataFinal,
                } as typeof updatedRow
            }

            return newRows
        })
    }

    useEffect(() => {
        setValue('dataInicial', dayjs(rows.at(0)?.dataInicial).toDate())
        setValue('dataFinal', dayjs(rows.at(0)?.dataFinal).toDate())
        setValue('proximaDataInicial', dayjs(rows.at(1)?.dataInicial).toDate())
        setValue('proximaDataFinal', dayjs(rows.at(1)?.dataFinal).toDate())
    }, [rows])

    return (
        <div>
            <ContentGuide.DataGrid
                key={rows.map(r => String(r.dataInicial)).join('-')}
                pageSize={3}
                hideFooter
                slots={{ Toolbar: () => null }}
                loading={isLoading}
                columns={[
                    {
                        field: 'id',
                        hide: true,
                    },
                    {
                        field: 'competencia',
                        headerName: 'Competência',
                        valueFormatter: ({ value }) =>
                            dayjs(value, 'YYYY-MM').format('MM/YYYY (MMM)'),
                        disableColumnMenu: true,
                    },
                    {
                        field: 'dataInicial',
                        headerName: 'Data inicial',
                        type: 'date',
                        editable: ({ row }) =>
                            row.index !== 0 && row.index !== 2,
                        disableColumnMenu: true,
                        valueFormatter: ({ value }) =>
                            dayjs(value).format(' DD/MM/YYYY        🔒'),
                    },
                    {
                        field: 'dataFinal',
                        headerName: 'Data final',
                        type: 'date',
                        editable: ({ row }) =>
                            row.index !== 2 && row.index !== 1,
                        disableColumnMenu: true,
                        valueFormatter: ({ value }) =>
                            dayjs(value).format(' DD/MM/YYYY        🔒'),
                    },
                    {
                        field: 'totalDias',
                        headerName: 'Dias',
                        type: 'number',
                        valueGetter: ({ row }) => {
                            const diff =
                                dayjs(row.dataFinal).diff(
                                    dayjs(row.dataInicial),
                                    'day',
                                ) + 1
                            return diff
                        },
                    },
                ]}
                rows={rows}
                onCellEditCommit={onCellEditCommit}
            />
        </div>
    )
}
